import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, CheckCircle2, Clock, Shield, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';

const PhoneScreenRepairPage = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Заміна екрану телефону в Одесі - CIMM | iPhone, Samsung, Xiaomi</title>
        <meta name="description" content="Заміна екрану iPhone, Samsung, Xiaomi в Одесі. Оригінальні дисплеї, ремонт за 30-60 хв. Ціни від 800 грн. ☎ +38 097 417 8788" />
      </Helmet>

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-orange-500">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Ноутбуки</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Контакти</Link>
          </nav>
          <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300">
            +38 097 417 8788
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          <Link to="/phones" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Повернутися до всіх послуг
          </Link>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-10 h-10 text-orange-500 icon-glow" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Заміна <span className="neon-text">екрану</span> телефону
            </h1>
            <p className="text-xl text-gray-400">iPhone, Samsung, Xiaomi, Huawei - ремонт за 30-60 хвилин</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 dark-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          
          {/* Key Benefits */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="glass-card p-6 text-center">
              <Clock className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">30-60 хв</h3>
              <p className="text-gray-400 text-sm">Експрес-ремонт</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Shield className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Гарантія</h3>
              <p className="text-gray-400 text-sm">До 6 місяців</p>
            </div>
            <div className="glass-card p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Оригінал</h3>
              <p className="text-gray-400 text-sm">Якісні дисплеї</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Wrench className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1">Тестування</h3>
              <p className="text-gray-400 text-sm">Сенсор і дисплей</p>
            </div>
          </div>

          {/* Description */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Коли потрібна заміна екрану?</h2>
              <ul className="space-y-4">
                {[
                  'Розбитий або тріснутий екран',
                  'Темні плями або білі лінії на дисплеї',
                  'Мерехтіння або рябіння зображення',
                  'Відсутність зображення при включенні',
                  'Вигоріння пікселів або плям',
                  'Механічне пошкодження матриці'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Image */}
              <div className="mt-6 rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1636589150123-6d57c10527ce" 
                  alt="Розбитий екран телефону"
                  className="w-full h-48 object-cover"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">Приклад пошкодженого екрану</p>
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Процес заміни</h2>
              <div className="space-y-4">
                {[
                  { step: '1', text: 'Діагностика', desc: 'Безкоштовно, 5-10 хвилин' },
                  { step: '2', text: 'Розбирання', desc: 'Акуратно відкриваємо корпус' },
                  { step: '3', text: 'Заміна модуля', desc: 'Встановлюємо новий дисплей' },
                  { step: '4', text: 'Тестування', desc: 'Перевіряємо тач і дисплей' },
                  { step: '5', text: 'Готово!', desc: 'Телефон працює як новий' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-500 font-bold">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold">{item.text}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Video Placeholder */}
              <div className="mt-6 bg-gray-900/50 rounded-lg p-6 border border-orange-500/20 text-center">
                <p className="text-gray-400 text-sm mb-2">📹 Відео процесу ремонту</p>
                <p className="text-xs text-gray-500">Дивіться наші роботи в <a href="https://www.instagram.com/cimmodua/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">Instagram</a></p>
              </div>
            </div>
          </div>

          {/* Detailed Process Description */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Детальний опис процесу</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-500">1</span>
                </div>
                <h3 className="font-bold text-white mb-2">Діагностика</h3>
                <p className="text-gray-400 text-sm">Майстер перевіряє тип пошкодження: тріщина тільки на склі, проблема з дисплеєм або сенсором. Визначаємо модель та тип необхідного модуля.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-500">2</span>
                </div>
                <h3 className="font-bold text-white mb-2">Розбирання</h3>
                <p className="text-gray-400 text-sm">За допомогою спеціальних інструментів акуратно відкриваємо корпус телефону. Від'єднуємо всі шлейфи та знімаємо пошкоджений модуль дисплея.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-500">3</span>
                </div>
                <h3 className="font-bold text-white mb-2">Встановлення</h3>
                <p className="text-gray-400 text-sm">Встановлюємо новий дисплейний модуль, під'єднуємо всі шлейфи, збираємо телефон. Тестуємо тач, дисплей, Face ID (якщо є).</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Орієнтовні ціни</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">iPhone</h3>
                <p className="text-gray-400 text-sm mb-4">iPhone 11-15 series</p>
                <p className="text-3xl font-bold text-orange-500">від 2500 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">Samsung</h3>
                <p className="text-gray-400 text-sm mb-4">Galaxy S, A series</p>
                <p className="text-3xl font-bold text-orange-500">від 1200 грн</p>
              </div>
              <div className="bg-gray-900/30 rounded-xl p-6 border border-orange-500/20">
                <h3 className="text-lg font-bold text-white mb-2">Xiaomi</h3>
                <p className="text-gray-400 text-sm mb-4">Redmi, Mi series</p>
                <p className="text-3xl font-bold text-orange-500">від 800 грн</p>
              </div>
            </div>
            <p className="text-center text-gray-400 text-sm mt-6">
              * Використовуємо оригінальні дисплеї та якісні копії. Діагностика безкоштовна.
            </p>
          </div>

          {/* FAQ */}
          <div className="glass-card p-8 mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Часті питання</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Скільки коштує заміна екрану iPhone?',
                  a: 'Ціна залежить від моделі. iPhone 11-13: від 2500 грн, iPhone 14-15: від 4000 грн. Точну ціну назвемо після діагностики.'
                },
                {
                  q: 'Чи втрачу я дані при заміні?',
                  a: 'Ні, всі дані, фото, контакти збережуться. Ми міняємо тільки дисплейний модуль.'
                },
                {
                  q: 'Яка різниця між оригіналом та копією?',
                  a: 'Оригінал - дисплей від виробника телефону. Копія - якісний аналог. Копії працюють добре, але трохи відрізняються кольорами.'
                },
                {
                  q: 'Чи можна замінити тільки скло?',
                  a: 'У сучасних телефонах скло склеєне з дисплеєм. Тому міняється весь модуль разом.'
                }
              ].map((item, idx) => (
                <details key={idx} className="bg-gray-900/30 rounded-lg border border-orange-500/20 overflow-hidden">
                  <summary className="p-4 cursor-pointer text-white font-semibold hover:bg-orange-500/10 transition-colors">
                    {item.q}
                  </summary>
                  <div className="px-4 pb-4 text-gray-300 border-t border-orange-500/20 pt-4">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="cta-card p-8 text-center cta-border">
            <h2 className="text-3xl font-bold text-white mb-4">Розбитий екран?</h2>
            <p className="text-gray-400 mb-6">Ми відремонтуємо за 30-60 хвилин!</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="tel:+380974178788">
                <Button className="futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0" size="lg">
                  Зателефонувати зараз
                </Button>
              </a>
              <Link to="/contacts">
                <Button variant="outline" className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent" size="lg">
                  Де ми знаходимось
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="cyber-footer py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center text-gray-400">
            <p>© 2026 CIMM Computers. Всі права захищені.</p>
            <p className="text-xs text-gray-500 mt-3 max-w-3xl mx-auto">Незалежний сервісний центр з постгарантійного обслуговування. Ми не є офіційним (авторизованим) сервісним центром і не пов'язані з виробниками техніки (Apple, Samsung, Sony, Xiaomi та ін.). Усі назви брендів належать їхнім власникам.</p>
            <p className="mt-2">
              <Link to="/privacy-policy" className="hover:text-orange-500 transition-colors">Політика конфіденційності</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PhoneScreenRepairPage;
